import { cn } from "@theprelude/core";
import {
  Button,
  CollapseDiagonalIcon,
  DownloadIcon,
  FrameIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from "@theprelude/ds";
import {
  getNodesBounds,
  getViewportForBounds,
  Node,
  useReactFlow,
} from "@xyflow/react";
import { toPng } from "html-to-image";
import { ReactNode, type JSX } from "react";
import { tb } from "@/lib/tracking";
import { DataPoint } from "@/queries/control-monitor";
import { DefaultTooltip } from "../../default-tooltip";
import { useControlMonitorNavigation } from "../control-monitor-navigation";

export function Toolbar({ nodes }: { nodes: Node<DataPoint>[] }) {
  const { view } = useControlMonitorNavigation();
  const { fitView, getNodes, zoomIn, zoomOut } = useReactFlow();

  const rootNode = nodes[0];

  const download = async () => {
    const imageWidth = 1024;
    const imageHeight = 768;

    const nodesBounds = getNodesBounds(getNodes());
    const viewport = getViewportForBounds(
      nodesBounds,
      imageWidth,
      imageHeight,
      0.5,
      2,
      0,
    );

    const el = document.querySelector(".react-flow__viewport");
    if (!el || !(el instanceof HTMLElement)) {
      return;
    }

    const data = await toPng(el, {
      backgroundColor: "#fff",
      width: imageWidth,
      height: imageHeight,
      style: {
        width: `${imageWidth}px`,
        height: `${imageHeight}px`,
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
      },
    });

    const a = document.createElement("a");
    a.setAttribute("download", "control-monitor.png");
    a.setAttribute("href", data);
    a.click();
  };

  return (
    <div className="flex rounded-md border border-stroke-line bg-surface-background p-0.5">
      <ToolbarButton
        icon={<ZoomInIcon />}
        label="Zoom in"
        onClick={() => zoomIn({ duration: 500 })}
      />
      <ToolbarButton
        icon={<ZoomOutIcon />}
        label="Zoom out"
        onClick={() => zoomOut({ duration: 500 })}
      />
      <ToolbarButton
        icon={<FrameIcon />}
        label="Center"
        onClick={() => fitView({ maxZoom: 1, duration: 500 })}
      />
      {rootNode && (
        <ToolbarButton
          icon={<CollapseDiagonalIcon />}
          label="Collapse all"
          onClick={async () => {
            await view(rootNode.data);
            setTimeout(() => fitView({ maxZoom: 1, duration: 500 }), 0);
          }}
        />
      )}
      <ToolbarButton
        icon={<DownloadIcon />}
        label="Download"
        onClick={download}
      />
    </div>
  );
}

const ToolbarButton = ({
  children,
  icon,
  label,
  active,
  onClick,
}: {
  children?: ReactNode;
  icon: JSX.Element;
  label: string;
  active?: boolean;
  onClick: () => void;
}) => (
  <DefaultTooltip asChild content={label}>
    <Button
      icon={icon}
      className={cn(
        "px-3 py-2 hover:bg-tertiary-foreground",
        active &&
          "bg-tertiary-background text-white hover:bg-tertiary-background",
        tb(label),
      )}
      onClick={onClick}
    >
      {children}
    </Button>
  </DefaultTooltip>
);
